import { useEffect, useRef, useState } from 'react';
import {
  MessageSquare, Plus, Trash2, Settings, ChevronLeft, ChevronRight, X,
  LogOut, ChevronUp, User, Gauge, Languages, Sun, Moon, Check, CircleAlert
} from 'lucide-react';
import chattyLogo from '../assets/chatty-logo.png';
import QuotaCard from './QuotaCard';

const CHAT_EXPIRY_WARNING_DAYS = 7;
const CHAT_RETENTION_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

// Helper function to extract name before '@', clean up dots/underscores, and capitalize
const formatDisplayName = (name) => {
  if (!name) return '';
  const localPart = name.split('@')[0];
  return localPart
    .replace(/[._]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

function getChatExpiryTimeMs(chat) {
  const ttl = Number(chat?.ttl);
  if (Number.isFinite(ttl) && ttl > 0) {
    return ttl * 1000;
  }

  const updatedAtMs = Date.parse(chat?.updatedAt || chat?.updated_at || '');
  if (Number.isFinite(updatedAtMs)) {
    return updatedAtMs + (CHAT_RETENTION_DAYS * DAY_MS);
  }

  return null;
}

export function isChatExpiringSoon(chat, nowMs = Date.now()) {
  const expiryTimeMs = getChatExpiryTimeMs(chat);
  if (!expiryTimeMs) return false;

  const timeUntilExpiryMs = expiryTimeMs - nowMs;
  return timeUntilExpiryMs > 0 && timeUntilExpiryMs <= CHAT_EXPIRY_WARNING_DAYS * DAY_MS;
}

export default function Sidebar({
  chatHistory, currentChatId,
  showDashboard, username, language, translation = {}, onNewChat, onLoadChat, onDeleteChat,
  onNavigateAdmin, onLogout,
  onToggleTheme, onChangeLanguage, isStreaming,
  isCollapsed, onToggleCollapse, isMobileOpen, onCloseMobile
}) {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [usageModalOpen, setUsageModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [languagePopupOpen, setLanguagePopupOpen] = useState(false);
  const [pendingDeleteChat, setPendingDeleteChat] = useState(null);
  const profileMenuRef = useRef(null);

  const formattedUsername = formatDisplayName(username);
  const [displayName, setDisplayName] = useState(formattedUsername);
  const [mailId, setMailId] = useState(username || '');

  useEffect(() => {
    if (username) {
      setDisplayName(formatDisplayName(username));
      setMailId(username);
    }
  }, [username]);

  useEffect(() => {
    if (!usageModalOpen && !profileModalOpen && !pendingDeleteChat) {
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setUsageModalOpen(false);
        setProfileModalOpen(false);
        setPendingDeleteChat(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [usageModalOpen, profileModalOpen, pendingDeleteChat]);

  useEffect(() => {
    if (!profileMenuOpen) {
      return;
    }

    const handlePointerDown = (event) => {
      if (!profileMenuRef.current?.contains(event.target)) {
        setProfileMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setProfileMenuOpen(false);
        setLanguagePopupOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [profileMenuOpen]);

  const closeMenu = () => {
    setProfileMenuOpen(false);
  };

  const handleSelectLanguage = (nextLanguage) => {
    if (isStreaming) return;
    onChangeLanguage?.(nextLanguage);
    setLanguagePopupOpen(false);
    closeMenu();
  };

  const openDeleteConfirm = (chat, event) => {
    event.stopPropagation();
    if (isStreaming) return;
    setPendingDeleteChat(chat);
  };

  const closeDeleteConfirm = () => {
    setPendingDeleteChat(null);
  };

  const confirmDeleteChat = (event) => {
    if (!pendingDeleteChat) return;
    onDeleteChat(pendingDeleteChat.id, event);
    setPendingDeleteChat(null);
  };

  const sidebarContent = (
    <div className={`flex flex-col h-full ${isCollapsed ? 'items-center p-3' : 'p-6'} bg-light-sidebar dark:bg-sidebar`}>
      <button
        onClick={onCloseMobile}
        className="md:hidden absolute top-4 right-4 p-2 rounded-lg transition hover:bg-black/10 text-light-text dark:hover:bg-white/10 dark:text-primary"
      >
        <X size={20} />
      </button>

      <div className={`relative flex w-full ${isCollapsed ? 'flex-col items-center gap-2 mb-6' : 'items-center gap-3 mb-8'}`}>
        <button
          type="button"
          onClick={onNewChat}
          disabled={isStreaming}
          className={`flex min-w-0 items-center gap-3 rounded-lg text-left transition hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:hover:bg-white/10 ${isCollapsed ? 'justify-center p-0' : 'w-full -m-2 p-2'}`}
          title={translation.newChat}
          aria-label={translation.newChat}
        >
          <img
            src={chattyLogo}
            alt="Chatty Logo"
            className="w-[80px] h-[80px] object-contain flex-shrink-0"
          />
          {!isCollapsed && (
            <span className="min-w-0 flex-1 text-left">
              <span className="block whitespace-nowrap text-2xl font-bold leading-tight text-light-text dark:text-primary">{translation.title}</span>
              <span className="block whitespace-nowrap text-xs leading-5 text-light-muted dark:text-secondary">{translation.subtitle}</span>
            </span>
          )}
        </button>
        <button
          onClick={onToggleCollapse}
          className={`hidden md:flex items-center justify-center rounded-lg transition ${isCollapsed ? 'w-10 h-8' : 'absolute -right-6 -top-6 w-8 h-8'} hover:bg-black/10 text-light-text dark:hover:bg-white/10 dark:text-primary`}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={20} strokeWidth={3} /> : <ChevronLeft size={20} strokeWidth={3} />}
        </button>
      </div>

      {/* ストリーミング中は履歴の保存先がずれないよう新規作成を止める。 */}
      {!isCollapsed ? (
        <div className="mb-3 rounded-lg px-2 py-1.5 transition bg-transparent">
          <button
            type="button"
            onClick={onNewChat}
            disabled={isStreaming}
            className="w-full group px-3 py-1.5 rounded-md cursor-pointer flex items-center gap-2 transition border border-transparent focus:outline-none bg-transparent text-light-text hover:bg-black/5 hover:text-light-text hover:border-black/10 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent disabled:hover:border-transparent dark:bg-thinking-primary dark:text-accent-text dark:hover:bg-thinking-secondary dark:hover:border-white/20 dark:disabled:hover:bg-thinking-primary dark:disabled:hover:border-transparent"
          >
            <Plus size={14} className="flex-shrink-0" />
            <span className="text-sm truncate">{translation.newChat}</span>
          </button>
        </div>
      ) : (
        <button
          onClick={onNewChat}
          disabled={isStreaming}
          className="w-10 h-10 p-0 mb-4 flex items-center justify-center rounded-lg transition bg-light-btn hover:bg-light-hover text-light-btn-text disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-light-btn dark:bg-thinking-primary dark:hover:bg-thinking-secondary dark:text-accent-text dark:disabled:hover:bg-thinking-primary"
          title={translation.newChat}
          aria-label={translation.newChat}
        >
          <MessageSquare size={18} />
        </button>
      )}

      {!isCollapsed && (
        <div className='flex-1 overflow-y-auto mb-4 pr-1 rounded-lg p-2 transition bg-transparent border border-gray-200 dark:border-0'>
          <h3 className='text-xs font-bold uppercase tracking-wider mb-3 px-3 transition text-gray-500 dark:text-[#F5EFE6]'>
            {translation.recentChats}
          </h3>

          <div className="space-y-0.5">
            {chatHistory.length === 0 ? (
              <p className='text-sm text-center px-3 transition text-gray-500 dark:text-primary/60'>{translation.noHistory}</p>
            ) : (
              chatHistory.map((chat) => {
                const isActiveChat = currentChatId === chat.id;
                const showExpiryWarning = isChatExpiringSoon(chat);
                // 生成中は別ルームへ切り替えず、表示中の会話と保存先を固定する。
                const isChatSwitchDisabled = isStreaming && !isActiveChat;
                return (
                  <div
                    key={chat.id}
                    onClick={() => {
                      if (!isStreaming) onLoadChat(chat);
                    }}
                    className={`group pl-3 pr-1 py-[4.0px] rounded-md flex justify-between items-center transition ${isChatSwitchDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} ${
                      isActiveChat
                        ? 'bg-black/5 text-light-text font-medium dark:bg-white/10 dark:text-white'
                        : 'bg-transparent text-light-text hover:bg-black/5 hover:text-light-text dark:text-[#F5EFE6] dark:hover:bg-white/10 dark:hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isActiveChat ? 'bg-green-500' : 'bg-transparent'}`} />
                      <p className="text-sm truncate">{chat.title}</p>
                    </div>
                    <div className="w-10 flex flex-shrink-0 items-center justify-end gap-1">
                      <span
                        aria-label={showExpiryWarning ? (translation.chatExpiresSoon || 'Chat expires soon') : undefined}
                        aria-hidden={!showExpiryWarning}
                        title={showExpiryWarning ? (translation.chatExpiresSoon || 'Chat expires soon') : undefined}
                        className={
                          'flex h-6 w-4 items-center justify-center transition ' +
                          (showExpiryWarning
                            ? 'text-gray-400 dark:text-white/45'
                            : 'invisible text-transparent')
                        }
                      >
                        <CircleAlert size={14} strokeWidth={2} />
                      </span>

                      <button
                        type="button"
                        onClick={(e) => openDeleteConfirm(chat, e)}
                        disabled={isStreaming}
                        className={
                          'p-1 rounded-md transition disabled:cursor-not-allowed disabled:opacity-60 ' +
                          (isActiveChat
                            ? 'opacity-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700 dark:text-white dark:hover:bg-white/20 dark:hover:text-white'
                            : 'opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:text-white/70 dark:hover:text-white dark:hover:bg-white/10')
                        }
                        title='Delete chat'
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {isCollapsed && <div className='flex-1' />}

      {username && (
        <div ref={profileMenuRef} className={`relative ${isCollapsed ? 'mb-2' : 'w-full mb-2'}`}>
          {profileMenuOpen && (
            <div
              className={
                'absolute z-30 w-56 rounded-lg border p-1.5 shadow-xl ' +
                (isCollapsed ? 'left-full bottom-0 ml-2' : 'bottom-full left-0 mb-2') +
                ' border-light-border bg-light-bg text-light-text dark:border-glass dark:bg-main dark:text-primary'
              }
            >
              <button
                type="button"
                onClick={() => onToggleTheme?.()}
                className="flex h-9 w-full items-center gap-2 rounded-md px-3 text-left text-sm font-medium transition hover:bg-black/5 dark:hover:bg-white/10"
              >
                <Moon size={16} className="dark:hidden" />
                <Sun size={16} className="hidden dark:block" />
                <span className="dark:hidden">{translation.lightMode || 'Light mode'}</span>
                <span className="hidden dark:inline">{translation.darkMode || 'Dark mode'}</span>
              </button>

              <div
                className="relative group/language"
                onMouseEnter={() => setLanguagePopupOpen(true)}
                onMouseLeave={() => setLanguagePopupOpen(false)}
              >
                <button
                  type="button"
                  onClick={() => setLanguagePopupOpen((open) => !open)}
                  className="flex h-9 w-full items-center justify-between gap-2 rounded-md px-3 text-left text-sm font-medium transition hover:bg-black/5 dark:hover:bg-white/10"
                >
                  <span className="flex items-center gap-2">
                    <Languages size={16} />
                    <span>{translation.language || 'Language'}</span>
                  </span>
                  <ChevronRight
                    size={16}
                    strokeWidth={2.5}
                    className="ml-auto flex-shrink-0 translate-x-1.5 text-light-muted dark:text-secondary"
                  />
                </button>

                {languagePopupOpen && (
                  <div className='absolute left-full top-0 z-40 ml-2 min-w-[120px] rounded-lg border p-1 shadow-xl before:absolute before:-left-2 before:top-0 before:h-full before:w-2 before:content-[""] border-light-border bg-light-bg text-light-text dark:border-glass dark:bg-main dark:text-primary'>
                    <button
                      type="button"
                      disabled={isStreaming}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        handleSelectLanguage('EN');
                      }}
                      className="flex h-9 w-full items-center justify-between rounded-md px-3 text-left text-sm font-medium transition hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:hover:bg-white/10 dark:disabled:hover:bg-transparent"
                    >
                      <span>English</span>
                      {language === 'EN' && <Check size={15} />}
                    </button>

                    <button
                      type="button"
                      disabled={isStreaming}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        handleSelectLanguage('日本語');
                      }}
                      className="flex h-9 w-full items-center justify-between rounded-md px-3 text-left text-sm font-medium transition hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:hover:bg-white/10 dark:disabled:hover:bg-transparent"
                    >
                      <span>日本語</span>
                      {language === '日本語' && <Check size={15} />}
                    </button>
                  </div>
                )}
              </div>

              <button
                type="button"
                disabled={isStreaming}
                onClick={() => {
                  if (isStreaming) return;
                  closeMenu();
                  setUsageModalOpen(true);
                }}
                className="flex h-9 w-full items-center gap-2 rounded-md px-3 text-left text-sm font-medium transition hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:hover:bg-white/10 dark:disabled:hover:bg-transparent"
              >
                <Gauge size={16} />
                <span>{translation.usageInfo || 'Usage Info'}</span>
              </button>

              <button
                type="button"
                disabled={isStreaming}
                onClick={() => {
                  if (isStreaming) return;
                  closeMenu();
                  setProfileModalOpen(true);
                }}
                className="flex h-9 w-full items-center gap-2 rounded-md px-3 text-left text-sm font-medium transition hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:hover:bg-white/10 dark:disabled:hover:bg-transparent"
              >
                <User size={16} />
                <span>{translation.profileTitle || 'Profile'}</span>
              </button>
              {/* 管理ダッシュボード */}
              {showDashboard && (
                <button
                  onClick={onNavigateAdmin}
                  disabled={isStreaming}
                  className="flex h-9 w-full items-center gap-2 rounded-md px-3 text-left text-sm font-medium transition hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:hover:bg-white/10 dark:disabled:hover:bg-transparent"
                  title={isCollapsed ? translation.admin : undefined}
                >
                  <Settings size={18} />
                  <span>{translation.admin}</span>
                </button>
              )}

              <button
                type="button"
                disabled={isStreaming}
                onClick={() => {
                  if (isStreaming) return;
                  closeMenu();
                  onLogout();
                }}
                className="flex h-9 w-full items-center gap-2 rounded-md px-3 text-left text-sm font-medium transition hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent dark:hover:bg-white/10 dark:disabled:hover:bg-transparent"
              >
                <LogOut size={16} />
                <span>{translation.logout}</span>
              </button>
            </div>
          )}

          <button
            type="button"
            onClick={() => setProfileMenuOpen((open) => !open)}
            className={`${isCollapsed ? 'w-10 h-10 justify-center p-0' : 'w-full h-11 gap-3 px-2'
              } flex items-center rounded-lg transition text-light-text hover:bg-black/5 dark:text-primary dark:hover:bg-white/10`}
            title={isCollapsed ? displayName || username : undefined}
            aria-haspopup="menu"
            aria-expanded={profileMenuOpen}
          >
            <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 bg-thinking-primary text-accent-text">
              {displayName ? displayName.charAt(0).toUpperCase() : username.charAt(0).toUpperCase()}
            </div>
            {!isCollapsed && (
              <>
                <span className="min-w-0 flex-1 truncate text-left text-sm font-medium">{displayName || username}</span>
                <ChevronUp
                  size={16}
                  className={`flex-shrink-0 transition ${profileMenuOpen ? 'rotate-180' : ''} text-light-muted dark:text-secondary`}
                />
              </>
            )}
          </button>
        </div>
      )}

      {/* Usage Modal */}
      {usageModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setUsageModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="usage-modal-title"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg border p-6 shadow-2xl border-light-border bg-light-bg text-light-text dark:border-glass dark:bg-main dark:text-primary"
          >
            <div className="mb-6 flex items-center justify-between gap-4">
              <h2 id="usage-modal-title" className="text-lg font-medium">{translation.usageInfo || 'Usage Info'}</h2>
              <button
                type="button"
                onClick={() => setUsageModalOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-lg transition hover:bg-black/5 dark:hover:bg-white/10"
                aria-label={translation.close || 'Close'}
              >
                <X size={18} />
              </button>
            </div>
            <QuotaCard isCollapsed={false} isHighlighted={false} />
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {profileModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setProfileModalOpen(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-modal-title"
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-lg border p-6 shadow-2xl border-light-border bg-light-bg text-light-text dark:border-glass dark:bg-main dark:text-primary"
          >
            <div className="mb-6 flex items-center justify-between gap-4">
              <h2 id="profile-modal-title" className="text-lg font-medium">{translation.profileTitle || 'Profile'}</h2>
              <button
                type="button"
                onClick={() => {
                  setDisplayName(formatDisplayName(username));
                  setProfileModalOpen(false);
                }}
                className="flex h-8 w-8 items-center justify-center rounded-lg transition hover:bg-black/5 dark:hover:bg-white/10"
                aria-label={translation.close || 'Close'}
              >
                <X size={18} />
              </button>
            </div>

            {/* Display Name */}
            <label className="mb-1 block text-xs font-medium text-light-muted dark:text-secondary">
              {translation.displayNameLabel || '表示名'}
            </label>
            <input
              type="text"
              value={displayName}
              readOnly
              className="mb-4 w-full rounded-lg border px-3 py-2 text-sm outline-none opacity-80 border-light-border bg-gray-100 text-light-text dark:border-glass dark:bg-main dark:text-primary"
            />

            {/* Mail Address */}
            <label className="mb-1 block text-xs font-medium text-light-muted dark:text-secondary">
              {translation.emailLabel || 'メールアドレス'}
            </label>
            <input
              type="text"
              value={mailId}
              readOnly
              className="mb-4 w-full rounded-lg border px-3 py-2 text-sm outline-none opacity-80 border-light-border bg-gray-100 text-light-text dark:border-glass dark:bg-main dark:text-primary"
            />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={`hidden md:block ${isCollapsed ? 'w-16' : 'w-64'} flex-shrink-0 transition-all duration-200`}>
        <div className="h-full">{sidebarContent}</div>
      </div>

      {isMobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={onCloseMobile} />
          <div className="relative w-72 h-full flex-shrink-0 shadow-xl">{sidebarContent}</div>
        </div>
      )}
      {pendingDeleteChat && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          onClick={closeDeleteConfirm}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-chat-title"
            className="w-full max-w-md rounded-lg border p-6 shadow-2xl border-light-border bg-light-bg text-light-text dark:border-glass dark:bg-main dark:text-primary"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mb-4 border-b border-gray-200 pb-2 dark:border-glass">
              <h2 id="delete-chat-title" className="text-lg font-bold">
                {translation.deleteChatTitle || 'Delete chat?'}
              </h2>
            </div>
            <div className="mt-3 rounded-md border border-light-border bg-light-input px-3 py-2 dark:border-glass dark:bg-main">
              <p className="text-xs font-semibold uppercase tracking-wide text-light-muted dark:text-secondary">
                {translation.deleteChatTarget || 'Chat room'}
              </p>
              <p className="mt-1 truncate text-sm font-semibold text-light-text dark:text-primary">
                {pendingDeleteChat.title}
              </p>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeDeleteConfirm}
                className="rounded-lg border border-gray-200 px-4 py-2 text-xs font-medium transition hover:bg-black/5 dark:border-glass dark:hover:bg-white/5"
              >
                {translation.cancel || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={confirmDeleteChat}
                className="rounded-lg border border-red-600 bg-red-600 px-4 py-2 text-xs font-medium text-white transition hover:bg-red-700 dark:border-red-500 dark:bg-red-600 dark:hover:bg-red-500"
              >
                {translation.deleteChat || 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
